import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { formatDateTime } from '../../utils/datetime';
import './ProcessRunReport.css';

// THE LAST PROCESS RUN AS A TABLE (run_report, written by the PEP run, the accounting-codes process and sync-pep-build-file).
//
// One row per data type — its outcome, the reason when it failed, and every file the run produced for it
// (the extracts it read, the combined file, the encrypted file) — plus the accounting file. Each file is a
// download: onDownload(key) asks the server for a 5-minute presigned URL at the moment it is clicked
// (GET /sync/{syncId}/task/{taskId}/process/file-link), which signs only keys this report lists.
//
// It replaces the one-paragraph comment on the Process Run card, which carried the same facts as a wall
// of text with nothing to click.

// IN THE ORDER THE RUN MAKES THEM, each labelled with the step folder it sits in (step-0-extracted/ …
// step-4-gzipped/) — read top to bottom as the file's journey: the extract(s), the processed copy, the
// combined file, the staged file (encrypted only for PEP-GUEST), and the gzip, which is what is delivered.
const STAGES = [
    { stage: 'extract', label: '0 · Extracted' },
    { stage: 'processed', label: '1 · Processed' },
    { stage: 'combined', label: '2 · Combined' },
    { stage: 'encrypted', label: '3 · Encrypted (PGP)' },
    { stage: 'final', label: '3 · Staged' },
    { stage: 'gzipped', label: '4 · Gzipped — delivered' },
    // A single task's file built on its own Run Process (sync-pep-build-file) — codes swapped, never delivered.
    { stage: 'built', label: 'XML · codes swapped' },
    { stage: 'excel', label: 'Excel · codes swapped' },
];
const STAGE_LABEL = { ...Object.fromEntries(STAGES.map(s => [s.stage, s.label])), accounting: '1 · Processed' };
const STAGE_ORDER = Object.fromEntries(STAGES.map((s, i) => [s.stage, i]));
const inOrder = (files) => [...(files || [])].sort((a, b) =>
    (STAGE_ORDER[a.stage] ?? 99) - (STAGE_ORDER[b.stage] ?? 99));
const STATUS_CLASS = { DELIVERED: 'is-ok', DONE: 'is-ok', BUILT: 'is-ok', ERROR: 'is-error', 'NOT DELIVERED': 'is-warn' };

const ProcessRunReport = ({ report, onDownload }) => {
    const [busy, setBusy] = useState(null);
    if (!report) return null;

    const download = async (key) => {
        if (busy) return;
        setBusy(key);
        try { await onDownload(key); } finally { setBusy(null); }
    };

    const fileLink = (f) => (
        <button
            key={f.key}
            type="button"
            className="prr-file"
            onClick={() => download(f.key)}
            disabled={busy === f.key}
            title={`Download ${f.name} — a link valid for 5 minutes is created when you click`}
        >
            <Download size={12} />
            <span className="prr-file-stage">{STAGE_LABEL[f.stage] || f.stage}</span>
            <span className="prr-file-name">{f.name}</span>
        </button>
    );

    const types = report.dataTypes || [];
    const delivered = types.filter(d => d.status === 'DELIVERED').length;
    const failed = types.filter(d => d.status === 'ERROR').length;
    // delivers: false — a run that builds files for download only (sync-pep-build-file); "0 of 1 delivered" would
    // read as a failure.
    const builds = report.delivers === false;

    return (
        <div className="prr">
            <div className="prr-summary">
                {types.length > 0 && builds && (
                    <span>
                        <strong>{types.filter(d => d.status === 'BUILT').length}</strong> file{types.length === 1 ? '' : 's'} built for download — not delivered to PEP
                    </span>
                )}
                {types.length > 0 && !builds && (
                    <span>
                        <strong>{delivered}</strong> of {types.length} data type{types.length === 1 ? '' : 's'} delivered
                        {failed > 0 && <>, <strong className="prr-bad">{failed} failed</strong></>}
                    </span>
                )}
                {report.finishedAt && <span className="prr-muted">finished {formatDateTime(report.finishedAt)}</span>}
            </div>
            {report.error && <div className="prr-error">{report.error}</div>}

            {/* VERIFY STAY RECORDS (ohip-stay-verify): what OHIP decided, and why it rejected what it rejected —
                grouped, so one missing rate code reads as one line with a count, not a page of records. */}
            {report.stayVerify && (
                <div className="prr-stayverify">
                    <div className="prr-summary">
                        <span className="prr-chip is-ok">{report.stayVerify.totals?.accepted || 0} accepted</span>
                        <span className={`prr-chip ${report.stayVerify.totals?.rejected ? 'is-error' : ''}`}>{report.stayVerify.totals?.rejected || 0} rejected</span>
                        {!!report.stayVerify.totals?.pending && <span className="prr-chip is-warn">{report.stayVerify.totals.pending} still processing</span>}
                        {!!report.stayVerify.totals?.check_failed && <span className="prr-chip is-warn">{report.stayVerify.totals.check_failed} could not be checked</span>}
                    </div>
                    {(report.stayVerify.reasons || []).length > 0 && (
                        <table className="prr-table">
                            <thead><tr><th>Rejection reason</th><th>Stays</th></tr></thead>
                            <tbody>
                                {report.stayVerify.reasons.map(r => (
                                    <tr key={r.reason}><td className="prr-outcome">{r.reason}</td><td>{r.count}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {report.accounting && report.accounting.error && (
                <div className="prr-accounting">
                    <span className="prr-label">Accounting codes</span>
                    <span className="prr-reason">Not built — {report.accounting.error}</span>
                </div>
            )}
            {report.accounting && report.accounting.key && (
                <div className="prr-accounting">
                    <span className="prr-label">Accounting codes</span>
                    {fileLink({ key: report.accounting.key, name: report.accounting.fileName, stage: 'accounting' })}
                    {report.accounting.summary && <span className="prr-muted">{report.accounting.summary}</span>}
                </div>
            )}

            {types.length > 0 && (
                <table className="prr-table">
                    <thead>
                        <tr>
                            <th>Data type</th>
                            <th>Outcome</th>
                            <th>Files</th>
                        </tr>
                    </thead>
                    <tbody>
                        {types.map(d => (
                            <tr key={d.dataType}>
                                <td className="prr-type">{d.dataType}</td>
                                <td className="prr-outcome">
                                    <span className={`prr-chip ${STATUS_CLASS[d.status] || ''}`}>{d.status}</span>
                                    {d.counts && (
                                        <div className="prr-muted">
                                            {d.counts.files} file{d.counts.files === 1 ? '' : 's'}
                                            {d.counts.converted ? `, ${d.counts.converted} converted` : ''}
                                            {d.counts.swapped ? `, ${d.counts.swapped} codes swapped` : ''}
                                            {d.counts.skipped ? `, ${d.counts.skipped} skipped` : ''}
                                        </div>
                                    )}
                                    {d.error && <div className="prr-reason">{d.error}</div>}
                                    {d.note && <div className="prr-muted">{d.note}</div>}
                                    {d.deliveredTo && <div className="prr-muted" title="Where PEP collects it">→ {d.deliveredTo}</div>}
                                </td>
                                <td className="prr-files">
                                    {(d.files || []).length ? inOrder(d.files).map(fileLink) : <span className="prr-muted">none</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default ProcessRunReport;
